import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface GeneratePdfOptions {
  /**
   * CSS selector for the container elements to capture (default: '.print-container')
   */
  containerSelector?: string;
  /**
   * Filename for the generated PDF (default: 'quick-start-guide.pdf')
   */
  filename?: string;
  /**
   * Wait time in milliseconds between capturing each section (default: 100)
   */
  waitTime?: number;
  /**
   * Scale factor for html2canvas (default: 2 for better quality)
   */
  scale?: number;
  /**
   * Callback function called with progress (0-100)
   */
  onProgress?: (progress: number) => void;
  /**
   * Maximum wait time in milliseconds for containers to appear (default: 5000)
   */
  maxWaitTime?: number;
}

/**
 * Waits for print containers to appear in the DOM
 */
async function waitForContainers(
  selector: string,
  maxWaitTime: number
): Promise<NodeListOf<HTMLElement>> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitTime) {
    const containers = document.querySelectorAll<HTMLElement>(selector);
    if (containers.length > 0) {
      return containers;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return document.querySelectorAll<HTMLElement>(selector);
}

/**
 * Generates a PDF from the current page by capturing each .print-container element
 * and combining them into a single PDF with one page per section.
 *
 * @param options - Configuration options for PDF generation
 * @returns Promise that resolves with the PDF blob
 */
export async function generatePdfFromDom(
  options: GeneratePdfOptions = {}
): Promise<Blob> {
  const {
    containerSelector = '.print-container',
    waitTime = 100,
    scale = 1,
    onProgress,
    maxWaitTime = 5000,
  } = options;

  if (typeof window === 'undefined') {
    throw new Error('PDF generation can only be performed in the browser');
  }

  // Wait for containers to appear (they might be loading)
  const containers = await waitForContainers(containerSelector, maxWaitTime);

  if (containers.length === 0) {
    throw new Error(
      `No elements found with selector: ${containerSelector}. ` +
        `Please ensure the page is in print mode or wait for content to load.`
    );
  }

  let pdf: jsPDF | null = null;

  // Store original styles to restore later
  const originalStyles: Array<{
    element: HTMLElement;
    display: string;
    visibility: string;
    height: string;
    width: string;
    maxWidth: string;
  }> = [];

  try {
    // Store original styles for all containers
    containers.forEach((container) => {
      originalStyles.push({
        element: container,
        display: container.style.display || '',
        visibility: container.style.visibility || '',
        height: container.style.height || '',
        width: container.style.width || '',
        maxWidth: container.style.maxWidth || '',
      });
    });

    // Process each container
    for (let i = 0; i < containers.length; i++) {
      const container = containers[i];

      // Update progress
      if (onProgress) {
        onProgress(Math.round(((i + 1) / containers.length) * 100));
      }

      // Hide all other containers and ensure current one is visible
      containers.forEach((el, index) => {
        if (index === i) {
          // Make sure current container is visible and properly positioned
          el.style.display = 'block';
          el.style.visibility = 'visible';
          el.style.height = 'auto';
          el.style.position = 'relative';
          el.style.overflow = 'visible';
        } else {
          // Hide other containers
          el.style.display = 'none';
          el.style.visibility = 'hidden';
          el.style.height = '0px';
        }
      });

      // Scroll container into view to ensure it's rendered
      // container.scrollIntoView({ behavior: 'instant', block: 'start' });

      // Force reflow to ensure layout is updated
      void container.offsetHeight;
      void container.getBoundingClientRect();

      // Wait a bit for layout to settle and images to load
      await new Promise((resolve) => setTimeout(resolve, waitTime));

      const elementRect = container.getBoundingClientRect();
      const elementWidth = elementRect.width;
      const elementHeight = container.scrollHeight || elementRect.height;

      // Extract all links from the container before capturing
      // We'll add them as clickable annotations after adding the image
      const links: Array<{
        href: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }> = [];

      const linkElements = container.querySelectorAll('a[href]');
      linkElements.forEach((linkEl) => {
        const anchor = linkEl as HTMLAnchorElement;
        const href = anchor.href;

        // Skip invalid or empty links
        const normalizedHref = href ? href.trim().toLowerCase() : '';
        if (
          !normalizedHref ||
          normalizedHref === '#' ||
          normalizedHref.startsWith('javascript:') ||
          normalizedHref.startsWith('data:') ||
          normalizedHref.startsWith('vbscript:')
        )
          return;

        // Get link position relative to container
        const linkRect = anchor.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate position relative to container (top-left is 0,0)
        const x = linkRect.left - containerRect.left;
        const y = linkRect.top - containerRect.top;
        const width = linkRect.width;
        const height = linkRect.height;

        links.push({ href, x, y, width, height });
      });

      // Capture the container as canvas at its natural rendered size
      // This preserves aspect ratio and prevents stretching
      const canvas = await html2canvas(container, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        // Don't specify width/height - let html2canvas capture at natural size
        // This preserves aspect ratio and prevents stretching
        allowTaint: false,
        removeContainer: true,
        imageTimeout: 15000,
      });

      const MAX_PDF_HEIGHT = 10000; // Maximum page height to avoid jsPDF limits

      const pdfWidth = elementWidth;
      const pdfHeight = elementHeight;

      // Check if we need to split vertically
      const needsVerticalSplit = pdfHeight > MAX_PDF_HEIGHT;
      const verticalPages = needsVerticalSplit
        ? Math.ceil(pdfHeight / MAX_PDF_HEIGHT)
        : 1;

      /*console.log(
        i,
        `Original: ${pdfWidth}x${pdfHeight}`,
        needsVerticalSplit
          ? `Split into ${verticalPages} vertical pages`
          : 'Single page'
      );*/

      // Process each vertical split
      for (let vPage = 0; vPage < verticalPages; vPage++) {
        // Calculate the portion of the canvas to extract (in CSS pixels)
        const sourceY = vPage * MAX_PDF_HEIGHT;
        const sourceHeight = Math.min(
          MAX_PDF_HEIGHT,
          pdfHeight - vPage * MAX_PDF_HEIGHT
        );

        // Create a temporary canvas for this portion
        const splitCanvas = document.createElement('canvas');
        splitCanvas.width = pdfWidth * scale;
        splitCanvas.height = sourceHeight * scale;
        const splitCtx = splitCanvas.getContext('2d');

        if (!splitCtx) continue;

        // Draw the portion of the original canvas
        // Canvas coordinates are in scaled pixels, so multiply by scale
        splitCtx.drawImage(
          canvas,
          0,
          sourceY * scale,
          pdfWidth * scale,
          sourceHeight * scale,
          0,
          0,
          pdfWidth * scale,
          sourceHeight * scale
        );

        const imgData = splitCanvas.toDataURL('image/png');

        const splitPdfWidth = pdfWidth;
        const splitPdfHeight = sourceHeight;
        const splitOrientation =
          splitPdfHeight <= splitPdfWidth ? 'landscape' : 'portrait';

        // Add new page with custom dimensions
        if (i === 0 && vPage === 0) {
          // For first page, use landscape if cover image exists OR if height <= width
          pdf = new jsPDF({
            orientation: splitOrientation,
            unit: 'px',
            format: [splitPdfWidth, splitPdfHeight],
          });
        } else {
          // For subsequent pages, use landscape if height <= width, otherwise portrait
          if (pdf) {
            pdf.addPage([splitPdfWidth, splitPdfHeight], splitOrientation);
          }
        }

        if (!pdf) continue;

        const currentPdf = pdf;

        currentPdf.addImage(
          imgData,
          'PNG',
          0,
          0,
          splitPdfWidth,
          splitPdfHeight,
          undefined,
          'FAST'
        );

        // Add clickable links on top of the image (only for links in this split)
        // Note: In PDF coordinates, (0,0) is at bottom-left, but we're using top-left
        // jsPDF uses top-left as origin, so y coordinates are correct
        links.forEach((link) => {
          // Check if link is within this split's vertical bounds
          const linkBottom = link.y + link.height;

          if (link.y >= sourceY && linkBottom <= sourceY + sourceHeight) {
            try {
              // Adjust link y coordinate relative to this split
              currentPdf.link(
                link.x,
                link.y - sourceY,
                link.width,
                link.height,
                {
                  url: link.href,
                }
              );
            } catch (error) {
              console.warn('Failed to add link to PDF:', link.href, error);
            }
          }
        });
      }
    }

    if (!pdf) throw new Error('Failed to create PDF');

    const pdfBlob = pdf.output('blob');

    return pdfBlob;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  } finally {
    // Restore original styles
    originalStyles.forEach(
      ({ element, display, visibility, height, width, maxWidth }) => {
        element.style.display = display;
        element.style.visibility = visibility;
        element.style.height = height;
        element.style.width = width;
        element.style.maxWidth = maxWidth;
      }
    );
  }
}
